import { Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import { getPartners } from "../../../../common/services/partners.api";
import Loading from "../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import ModalEditPartners from "../modals/ModalEditPartners";
// import "../css/partial-partners-tablne.scss";
import { ViewType } from "../small-partner-roles/view-type";
import {
  formatDisputeOrInvoice,
  getActionsByViewType,
  getChargeStatusLabel,
  getEntityByViewType,
  getHeadersByViewType,
  getPlaceholderByViewType,
  getRendersByViewType,
  getStatusOptionsByViewType,
  getViewTitle,
  loadData,
} from "../small-partner-roles/get-headers--view-type";
import {
  formatDate,
  formatDateAndTime,
} from "../../../../common/helpers/date.helpers";
import ModalDisputeInvoice from "../modals/ModalDisputeInvoice";
import ModalDigitalCertificate from "../modals/ModalDigitalCertificate";
import { PARTNER_STATUS } from "@/types/partners.type";
import { useCompanyStore } from "@/common/states/store/Company";
import { getChargePaged } from "@/common/services/partner-charge.api";

const STATUS_LABELS = {
  [PARTNER_STATUS.ACTIVE]: "Ativo",
  [PARTNER_STATUS.INACTIVE]: "Inativo",
  [PARTNER_STATUS.DEFAULTER]: "Inadimplente",
};

const STATUS_COLORS = {
  [PARTNER_STATUS.ACTIVE]: "font-semibold text-greenPrimary",
  [PARTNER_STATUS.INACTIVE]: "font-semibold text-gray",
  [PARTNER_STATUS.DEFAULTER]: "font-semibold text-redPrimary",
};

function getStatusLabels(status: PARTNER_STATUS) {
  return (
    <span
      className={`${STATUS_COLORS[status]}`}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const EMPTY_ACTION = { eventName: "", data: undefined };

function TablePartners() {
  const [currentView, setCurrentView] = useState<ViewType>(ViewType.PARTNERS);

  // const companyId = getCompanyId();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [custom, setCustom] = useState<any[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleDisputeInvoiceChange(updatedItem: any) {
    setCustom((prev) =>
      prev.map((item) => {
        const prevId = item._original?._id || item._id;
        const updatedId = updatedItem._original?._id || updatedItem._id;
        if (prevId === updatedId) {
          return formatDisputeOrInvoice(currentView, updatedItem);
        }
        return item;
      }),
    );
  }

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION,
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });

  const currentHeaders = getHeadersByViewType(currentView);
  const company = useCompanyStore();

  useEffect(() => {
    const config = {
      [ViewType.PARTNERS]: {
        apiCall: getPartners,
        setStateFn: setCustom,
        dataTransformer: (data) =>
          data.map((partner) => ({
            ...partner,
            isCertificate: !!partner.certificate || !!partner.isCertificate,
            companyName: (
              <a
                href={`/parceiros/${partner._id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/parceiros/${partner._id}`);
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {partner.companyName}
              </a>
            ),

            updatedAt: formatDateAndTime(partner.updatedAt),
            createdAt: formatDateAndTime(partner.createdAt),
            ownerName: partner.owner
              ? partner.owner.name
              : "Proprietário Desconhecido",
          })),
      },

      [ViewType.DISPUTES]: {
        apiCall: getChargePaged,
        setStateFn: setCustom,
        dataTransformer: (data) =>
          data.map((dispute) => ({
            status: dispute.contest?.status
              ? dispute.contest.status === "open"
                ? "Aberto"
                : "Fechado"
              : "-",
            partName: dispute?.partneName || "",
            notes: dispute?.notes || "",
            message: dispute?.contest?.message,
            date: formatDate(dispute?.contest?.date) || "",
            messageResponse: dispute?.contest?.messageResponse || "-",
            _original: dispute,
          })),
      },

      [ViewType.INVOICES]: {
        apiCall: getChargePaged,
        setStateFn: setCustom,
        dataTransformer: (data) =>
          data.map((dispute) => ({
            partName: dispute.partneName,
            status: getChargeStatusLabel(dispute.status),
            notes: dispute.notes,
            dueDate: formatDate(dispute.dueDate),
            externalId: dispute.externalId,
            _original: dispute,
          })),
      },
    };

    const currentConfig = config[currentView];

    loadData({
      apiCall: currentConfig.apiCall,
      setStateFn: currentConfig.setStateFn,
      dataTransformer: currentConfig.dataTransformer,
      filters,
      page,
      setData,
      data,
      setIsLoading,
      notifyError,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, filters, page, company]);

  const isAction = (eventName: string) => action.eventName === eventName;
  const actions = getActionsByViewType(currentView, setAction, notifyError);
  const renders = getRendersByViewType(currentView, navigate);

  const cleanActionData = () => setAction(EMPTY_ACTION);

  if (isAction("viewInvoice")) {
    cleanActionData();
    const invoiceId = action.data as string;
    if (invoiceId.startsWith("pay_")) {
      const id = invoiceId.replace("pay_", "");
      const link = `https://www.asaas.com/i/${id}`;
      window.open(link, "_blank");
    }
  }
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader={getViewTitle(currentView)}
          placeholder={getPlaceholderByViewType(currentView)}
          statusOptions={getStatusOptionsByViewType(currentView)}
          newItemLabel="Novo Parceiro"
          onNewItemClick={() =>
            setAction({
              eventName: "edit",
              data: undefined,
            })
          }
          onSearch={handleFilterChange}
          entity={getEntityByViewType(currentView)}
          tableData={custom}
        />
        <div className="table-section rounded-lg bg-white">
          <div className="flex mb-4 space-x-2">
            <button
              className={`py-2 px-4 ${currentView === ViewType.PARTNERS
                  ? "bg-[#E44131] text-white"
                  : "bg-gray-200"
                }`}
              onClick={() => setCurrentView(ViewType.PARTNERS)}
            >
              Parceiros
            </button>
            <button
              className={`py-2 px-4 ${currentView === ViewType.INVOICES
                  ? "bg-[#E44131] text-white"
                  : "bg-gray-200"
                }`}
              onClick={() => setCurrentView(ViewType.INVOICES)}
            >
              Faturas
            </button>
            <button
              className={`py-2 px-4 ${currentView === ViewType.DISPUTES
                  ? "bg-[#E44131] text-white"
                  : "bg-gray-200"
                }`}
              onClick={() => setCurrentView(ViewType.DISPUTES)}
            >
              Contestações
            </button>
          </div>

          <GenericTable
            headers={currentHeaders}
            data={custom}
            actions={actions}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                ...renders,
                formatDate,
                formatDateAndTime,
                status: getStatusLabels,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity={getEntityByViewType(currentView)}
          />

          {isLoading && <Loading />}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 10)}
            goToPage={setPage}
          />

          {isAction("changeStatus") && (
            <ModalDisputeInvoice
              onChance={handleDisputeInvoiceChange}
              data={action.data}
              onClose={cleanActionData}
            />
          )}
          {isAction("edit") && (
            <ModalEditPartners
              partnersId={action.data as string}
              onClose={cleanActionData}
            />
          )}
          {isAction("uploadCertificate") && (
            <ModalDigitalCertificate
              partnersId={action.data as string}
              onClose={cleanActionData}
            />
          )}
        </div>
      </div>
    </Suspense>
  );
}

export default TablePartners;
